import React, { useRef, useState, useEffect } from 'react'

import * as Styled from './styles'

interface Props {
  hideButtons?: boolean
  children: React.ReactNode
  selectedIndex?: number
}

const ScrollWrapperWithControls: React.FC<Props> = ({ hideButtons = false, children, selectedIndex }) => {
  const ref = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLDivElement | null>(null)

  const [hasHorizontalScrollBar, setHasHorizontalScrollBar] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  const [isScrolledToEnd, setIsScrolledToEnd] = useState(false)

  // Return the inner list element (e.g. the TabList) which is expected to be the
  // first child of the wrapper we render. This preserves previous behavior that
  // operated on `ref.current.childNodes[0]`.
  const getElementList = (): HTMLElement | null => (listRef.current?.firstElementChild as HTMLElement) || null

  useEffect(() => {
    const elementList = getElementList()
    if (!elementList) return

    const element = elementList as HTMLElement

    function checkScrollBar(): void {
      setHasHorizontalScrollBar(element.scrollWidth > element.getBoundingClientRect().width)
    }

    function onScroll(): void {
      setIsScrolled(element.scrollLeft > 0)
      setIsScrolledToEnd(element.offsetWidth + element.scrollLeft >= element.scrollWidth)
    }

    checkScrollBar()

    window.addEventListener('resize', checkScrollBar)
    element.addEventListener('scroll', onScroll)

    // initial state
    onScroll()

    return () => {
      window.removeEventListener('resize', checkScrollBar)
      element.removeEventListener('scroll', onScroll)
    }
  }, [ref])

  useEffect(() => {
    if (typeof selectedIndex !== 'number') return
    const elementList = getElementList()
    if (!elementList) return

    const target = elementList.children[selectedIndex] as HTMLElement | undefined
    if (!target) return

    const targetLeft = target.offsetLeft
    const targetWidth = target.offsetWidth
    const containerWidth = elementList.clientWidth

    const scrollLeft = targetLeft - (containerWidth - targetWidth) / 2
    const newScrollLeft = Math.max(0, Math.min(scrollLeft, elementList.scrollWidth - containerWidth))

    const targetTop = target.offsetTop
    const targetHeight = target.offsetHeight
    const containerHeight = elementList.clientHeight

    const scrollTop = targetTop - (containerHeight - targetHeight) / 2
    const newScrollTop = Math.max(0, Math.min(scrollTop, elementList.scrollHeight - containerHeight))

    elementList.scrollTo({ left: newScrollLeft, top: newScrollTop, behavior: 'smooth' })
  }, [selectedIndex])

  return (
    <Styled.ScrollWrapperWithControls
      ref={ref}
      $hideButtons={hideButtons}
      $hasHorizontalScrollBar={hasHorizontalScrollBar}
      $isScrolled={isScrolled}
      $isScrolledToEnd={isScrolledToEnd}
    >
      <div ref={listRef} className="scroll-list">
        {children}
      </div>
      {!hideButtons && hasHorizontalScrollBar && (
        <>
          {isScrolled && (
            <i
              className="far fa-chevron-left"
              aria-hidden="true"
              onClick={() => {
                getElementList()?.scrollBy({
                  left: -120,
                  behavior: 'smooth',
                })
              }}
            />
          )}
          {!isScrolledToEnd && (
            <i
              className="far fa-chevron-right"
              aria-hidden="true"
              onClick={() => {
                getElementList()?.scrollBy({
                  left: 120,
                  behavior: 'smooth',
                })
              }}
            />
          )}
        </>
      )}
    </Styled.ScrollWrapperWithControls>
  )
}

export default ScrollWrapperWithControls
